import { createContext, useContext, useState, useEffect } from "react";
import { collection, getDocs, addDoc, doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { db, auth, FIREBASE_READY } from "../firebase";
import { INIT_POSTS, INIT_ABOUT, INIT_PROJS } from "../data/initialData";
import { sLoad } from "../utils/storage";

const AppContext = createContext(null);

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}

export function AppProvider({ children }) {
  const [posts,       setPosts]       = useState(INIT_POSTS);
  const [about,       setAbout]       = useState(INIT_ABOUT);
  const [projects,    setProjects]    = useState(INIT_PROJS);
  const [landowners,  setLandowners]  = useState([]);
  const [isAdmin,     setIsAdmin]     = useState(false);
  const [loaded,      setLoaded]      = useState(false);

  useEffect(() => {
    if (!FIREBASE_READY) {
      Promise.all([
        sLoad("lhda_posts",    INIT_POSTS),
        sLoad("lhda_about",    INIT_ABOUT),
        sLoad("lhda_projects", INIT_PROJS),
      ]).then(([p, a, r]) => { setPosts(p); setAbout(a); setProjects(r); setLoaded(true); });
      return;
    }

    (async () => {
      const [postsSnap, projsSnap, aboutSnap] = await Promise.all([
        getDocs(collection(db, "posts")),
        getDocs(collection(db, "projects")),
        getDoc(doc(db, "about", "main")),
      ]);
      if (postsSnap.empty) {
        for (const { id: _id, ...data } of INIT_POSTS) await addDoc(collection(db, "posts"), data);
      }
      if (projsSnap.empty) {
        for (const { id: _id, ...data } of INIT_PROJS) await addDoc(collection(db, "projects"), data);
      }
      if (!aboutSnap.exists()) await setDoc(doc(db, "about", "main"), INIT_ABOUT);
    })().then(() => setLoaded(true)).catch(() => setLoaded(true));

    const unsubPosts = onSnapshot(collection(db, "posts"), snap => {
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubProjs = onSnapshot(collection(db, "projects"), snap => {
      setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubAbout = onSnapshot(doc(db, "about", "main"), snap => {
      if (snap.exists()) setAbout(snap.data());
    });
    const unsubLandowners = onSnapshot(collection(db, "landowners"), snap => {
      setLandowners(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubPosts(); unsubProjs(); unsubAbout(); unsubLandowners(); };
  }, []);

  useEffect(() => {
    if (!FIREBASE_READY) return;
    return onAuthStateChanged(auth, user => setIsAdmin(!!user));
  }, []);

  const handleLogout = async () => {
    if (FIREBASE_READY) await signOut(auth);
    setIsAdmin(false);
  };

  return (
    <AppContext.Provider value={{
      posts, setPosts,
      about, setAbout,
      projects, setProjects,
      landowners, setLandowners,
      isAdmin, setIsAdmin,
      handleLogout,
      loaded,
    }}>
      {children}
    </AppContext.Provider>
  );
}
